import { useEffect, useState } from 'react';
import { Msg } from '@/store/slices/room';
import { ConversationFeedback } from '@/app/api';

import logger from '@/utils/logger';

/**
 * @brief 将字符串包装成 TLV
 */
export const string2tlv = (str: string, type: string) => {
  const typeBuffer = new Uint8Array(4);

  for (let i = 0; i < type.length; i++) {
    typeBuffer[i] = type.charCodeAt(i);
  }

  const lengthBuffer = new Uint32Array(1);
  const valueBuffer = new TextEncoder().encode(str);

  lengthBuffer[0] = valueBuffer.length;

  const tlvBuffer = new Uint8Array(typeBuffer.length + 4 + valueBuffer.length);

  tlvBuffer.set(typeBuffer, 0);

  tlvBuffer[4] = (lengthBuffer[0] >> 24) & 0xff;
  tlvBuffer[5] = (lengthBuffer[0] >> 16) & 0xff;
  tlvBuffer[6] = (lengthBuffer[0] >> 8) & 0xff;
  tlvBuffer[7] = lengthBuffer[0] & 0xff;

  tlvBuffer.set(valueBuffer, 8);
  return tlvBuffer.buffer;
};

/**
 * @brief TLV 数据格式转换成字符串
 * @note TLV 数据格式
 * | magic number | length(big-endian) | value |
 * @param {ArrayBufferLike} tlvBuffer
 * @returns
 */
export const tlv2String = (tlvBuffer: ArrayBufferLike) => {
  const typeBuffer = new Uint8Array(tlvBuffer, 0, 4);
  const lengthBuffer = new Uint8Array(tlvBuffer, 4, 4);
  const valueBuffer = new Uint8Array(tlvBuffer, 8);

  let type = '';
  for (let i = 0; i < typeBuffer.length; i++) {
    type += String.fromCharCode(typeBuffer[i]);
  }

  const length =
    (lengthBuffer[0] << 24) | (lengthBuffer[1] << 16) | (lengthBuffer[2] << 8) | lengthBuffer[3];

  const value = new TextDecoder().decode(valueBuffer.subarray(0, length));

  return { type, value };
};

export const isMobile = () =>
  /Mobi|Android|iPhone|iPad|Windows Phone/i.test(window.navigator.userAgent) ||
  window?.innerWidth < 767;

export function useIsMobile() {
  const getIsMobile = () =>
    /Mobi|Android|iPhone|iPad|Windows Phone/i.test(window.navigator.userAgent) ||
    window.innerWidth < 767;

  const [isMobile, setIsMobile] = useState(getIsMobile());

  useEffect(() => {
    const handleResize = () => {
      const value = getIsMobile();
      setIsMobile(value);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

/**
 * 构建反馈数据
 */
export const buildFeedbackData = (
  msgHistory: Msg[],
  botName: string,
  sceneInfo: {
    name: string;
    roomId: string;
    taskId: string;
  }
): ConversationFeedback => {
  logger.info('[buildFeedbackData] 调试信息:', {
    botName,
    messageCount: msgHistory.length,
    messages: msgHistory.map((msg) => ({
      user: msg.user,
      value: msg.value,
      isBot: msg.user === botName,
    })),
  });

  // 确保消息按时间排序
  const sortedHistory = [...msgHistory].sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
  );

  // 计算统计数据
  const stats = sortedHistory.reduce(
    (acc, msg) => ({
      totalMessages: acc.totalMessages + 1,
      userMessages: acc.userMessages + (msg.user !== botName ? 1 : 0),
      assistantMessages: acc.assistantMessages + (msg.user === botName ? 1 : 0),
      likeCount: acc.likeCount + (msg.feedback === 'like' ? 1 : 0),
      dislikeCount: acc.dislikeCount + (msg.feedback === 'dislike' ? 1 : 0),
      interruptCount: acc.interruptCount + (msg.isInterrupted ? 1 : 0),
    }),
    {
      totalMessages: 0,
      userMessages: 0,
      assistantMessages: 0,
      likeCount: 0,
      dislikeCount: 0,
      interruptCount: 0,
    }
  );

  // 确保所有时间戳都使用ISO格式
  const formatTimestamp = (time: any): string => {
    if (!time) return new Date().toISOString();

    // 如果已经是ISO字符串，直接返回
    if (typeof time === 'string' && time.includes('T') && time.includes('Z')) {
      return time;
    }

    // 尝试转换为Date对象
    try {
      const date = new Date(time);
      if (isNaN(date.getTime())) {
        logger.warn('[Utils] Invalid timestamp:', time, 'using current time');
        return new Date().toISOString();
      }
      return date.toISOString();
    } catch (e) {
      logger.warn('[Utils] Timestamp conversion error:', e, 'using current time');
      return new Date().toISOString();
    }
  };

  return {
    sessionInfo: {
      startTime: formatTimestamp(sortedHistory[0]?.time),
      endTime: new Date().toISOString(),
      sceneInfo,
    },
    conversations: sortedHistory.map((msg) => {
      const role = msg.user === botName ? 'assistant' : 'user';
      logger.info('[buildFeedbackData] 消息角色判断:', {
        msgUser: msg.user,
        botName,
        isEqual: msg.user === botName,
        finalRole: role,
        content: msg.value,
      });
      return {
        timestamp: formatTimestamp(msg.time),
        role,
        content: msg.value,
        metadata: {
          isInterrupted: msg.isInterrupted,
          definite: msg.definite,
          paragraph: msg.paragraph,
          feedback: msg.feedback,
        },
      };
    }),
    feedbackStats: stats,
  };
};
